<script setup>
import { computed, ref } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { readFileAsync } from '@/composables/file-upload'
import { useNotify } from '@/composables/notify'
import { useDetectDevice } from '@/composables/device'
import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore, infoBoxToneEnum, infoBoxIdEnum } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import InfoBox from '@/components/shared/InfoBox.vue'
import NavigateBack from '@/components/shared/NavigateBack.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import WarehouseSelector from '@/components/shared/WarehouseSelector.vue'

const props = defineProps({
  serviceType: {
    type: Number,
    required: true,
  },
})
const emit = defineEmits(['update', 'cancel'])

const i18n = getI18n()
const planningStore = usePlanningStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const { notifySuccess } = useNotify()
const { isMobile } = useDetectDevice()
const { serviceImportTargetEnum, serviceImportMultiColumnTargets, serviceImportCodeCollisionModeEnum } = planningStore
const { serviceTypeEnum } = portalStore

const isStaff = props.serviceType === serviceTypeEnum.STAFF
const labelKeys = isStaff
  ? {
    title: 'common.staffImport.title',
    fileHint: 'common.staffImport.fileHint',
    count: 'common.staffImport.count',
    confirm: 'common.staffImport.confirm',
    done: 'common.staffImport.done',
    locationHint: 'common.staffImport.locationHint',
    defaultLocation: 'common.staffImport.defaultWorkplace',
  }
  : {
    title: 'common.equipmentImport.title',
    fileHint: 'common.equipmentImport.fileHint',
    count: 'common.equipmentImport.equipmentCount',
    confirm: 'common.equipmentImport.confirm',
    done: 'common.equipmentImport.done',
    locationHint: 'common.equipmentImport.locationHint',
    defaultLocation: 'common.equipmentImport.defaultLocation',
  }
const nameTargets = isStaff
  ? [serviceImportTargetEnum.NAME, serviceImportTargetEnum.FULL_NAME]
  : [serviceImportTargetEnum.NAME]

const importStepEnum = Object.freeze({ FILE: 'file', MAPPING: 'mapping', LOCATION: 'location', SUMMARY: 'summary' })
const importSteps = Object.values(importStepEnum)

const step = ref(importStepEnum.FILE)
const file = ref(null)
const fileData = ref(null)
const analysis = ref(null)
const targets = ref([])
const confirmations = ref({})
const warehouse = ref(webSiteStore.getDefaultWarehouse())
const collisionMode = ref(serviceImportCodeCollisionModeEnum.SKIP)
const numberFormat = ref(null)
const dateFormat = ref(null)
const summary = ref(null)
const loading = ref(false)
const samplePage = ref(0)

const sampleRows = computed(() => analysis.value?.sample_rows || [])
const sampleRowCount = computed(() => sampleRows.value.length)
const FIRST_DATA_ROW_NUMBER = 2

const targetOptions = planningStore.getLocalizedServiceImportTargets(props.serviceType)
const numberFormatOptions = planningStore.getServiceImportNumberFormats()
const dateFormatOptions = planningStore.getServiceImportDateFormats()

const confirmationColumns = computed(() => (analysis.value?.columns || [])
  .map((column, index) => ({ ...column, index }))
  .filter(column => column.needs_confirmation))
const isNameMapped = computed(() => nameTargets.some(target => targets.value.includes(target)))
const detectedOtherType = computed(() => analysis.value?.service_type != null && analysis.value.service_type !== props.serviceType)
const isLocationMapped = computed(() => targets.value.includes(serviceImportTargetEnum.WAREHOUSE))
const isMappingValid = computed(() => isNameMapped.value && confirmationColumns.value.every(column => confirmations.value[column.index] != null))

const isAnalysisMatched = computed(() => analysis.value?.is_reelsync || analysis.value?.matched_count > 0)
const fieldClass = (desktopCols) => isMobile.value ? 'col-12' : `col-${desktopCols}`

const summaryFields = computed(() => {
  if (!summary.value) return []
  const { service_count: serviceCount, location_from_file_count: locationFromFileCount } = summary.value
  return [
    { label: labelKeys.count, value: serviceCount },
    ...(isStaff ? [] : [
      { label: 'common.equipmentImport.serializedCount', value: summary.value.serialized_count },
      { label: 'common.equipmentImport.stockQuantity', value: summary.value.stock_quantity },
    ]),
    { label: 'common.equipmentImport.inactiveCount', value: summary.value.inactive_count },
    ...(isLocationMapped.value ? [
      { label: 'common.equipmentImport.locationFromFile', value: locationFromFileCount },
      { label: labelKeys.defaultLocation, value: serviceCount - locationFromFileCount },
    ] : []),
    ...(summary.value.groups_created.length || summary.value.groups_matched.length ? [
      { label: 'common.equipmentImport.groupsCreated', value: summary.value.groups_created.length },
      { label: 'common.equipmentImport.groupsMatched', value: summary.value.groups_matched.length },
    ] : []),
  ]
})

const summarySections = computed(() => {
  if (!summary.value) return []
  const {
    issues, group_description_conflicts: groupDescriptionConflicts = [], unknown_locations: unknownLocations,
    merged, existing_codes: existingCodes, recoded, combinations_skipped: combinationsSkipped = [], new_tags: newTags,
  } = summary.value
  return [
    {
      key: 'issues',
      icon: 'mdi-alert-circle-outline',
      label: 'common.equipmentImport.issues',
      count: issues.length,
      attention: true,
      rows: issues.map(issue => [
        `${i18n.t('common.row', 1)} ${issue.row}`,
        issue.column,
        i18n.t(issue.code, { value: issue.value, max: issue.max }),
      ]),
    },
    {
      key: 'groupDescriptionConflicts',
      icon: 'mdi-alert-circle-outline',
      label: 'common.equipmentImport.groupDescriptionConflicts',
      count: groupDescriptionConflicts.length,
      attention: true,
      chips: groupDescriptionConflicts,
    },
    {
      key: 'unknownLocations',
      icon: 'mdi-map-marker-question-outline',
      label: 'common.equipmentImport.unknownLocations',
      count: unknownLocations.length,
      chips: unknownLocations,
    },
    {
      key: 'merged',
      icon: 'mdi-call-merge',
      label: 'common.equipmentImport.merged',
      count: merged.reduce((count, item) => count + item.rows.length, 0),
      rows: merged.map(item => [item.key, `${i18n.t('common.row', 2)} ${item.rows.join(', ')}`]),
    },
    {
      key: 'existingCodes',
      icon: 'mdi-content-duplicate',
      label: 'common.equipmentImport.existingCodes',
      count: existingCodes.length,
      chips: existingCodes,
    },
    {
      key: 'recoded',
      icon: 'mdi-swap-horizontal',
      label: 'common.equipmentImport.recoded',
      count: recoded.length,
      chips: recoded.map(item => `${item.original} → ${item.code}`),
    },
    {
      key: 'combinationsSkipped',
      icon: 'mdi-link-variant-off',
      label: 'common.equipmentImport.combinationsSkipped',
      count: combinationsSkipped.length,
      chips: combinationsSkipped,
    },
    {
      key: 'newTags',
      icon: 'mdi-tag-multiple-outline',
      label: 'common.equipmentImport.newTags',
      count: newTags.length,
      chips: newTags,
    },
  ].filter(section => section.count)
})

const stepDone = (name) => importSteps.indexOf(step.value) > importSteps.indexOf(name)

const getTargetOptions = (index) => targetOptions.filter(option => analysis.value?.allowed_targets.includes(option.id)).map(option => ({
  ...option,
  disable: !serviceImportMultiColumnTargets.includes(option.id) && targets.value.some((target, i) => i !== index && target === option.id),
}))

const onFileSelected = async(selectedFile) => {
  file.value = selectedFile
  analysis.value = null
  summary.value = null
  samplePage.value = 0
  if (!selectedFile) return

  loading.value = true
  try {
    fileData.value = await readFileAsync(selectedFile)
    analysis.value = (await planningStore.analyzeServiceImportAsync(props.serviceType, { file: fileData.value })).data
    targets.value = analysis.value.columns.map(column => column.target)
    numberFormat.value = analysis.value.number_format
    dateFormat.value = analysis.value.date_format
    confirmations.value = {}
  } finally {
    loading.value = false
  }
}

const onConfirmationAnswered = (column, accepted) => {
  confirmations.value = { ...confirmations.value, [column.index]: accepted }
  targets.value[column.index] = accepted ? column.target : serviceImportTargetEnum.IMPORTED_DATA
}

const buildRequest = (dryRun) => ({
  file: fileData.value,
  targets: targets.value,
  number_format: numberFormat.value,
  date_format: dateFormat.value,
  warehouse: warehouse.value,
  collision_mode: collisionMode.value,
  dry_run: dryRun,
})

const onLocationContinue = async() => {
  loading.value = true
  try {
    summary.value = (await planningStore.importServicesAsync(props.serviceType, buildRequest(true))).data
    step.value = importStepEnum.SUMMARY
  } finally {
    loading.value = false
  }
}

const onImport = async() => {
  loading.value = true
  try {
    const result = (await planningStore.importServicesAsync(props.serviceType, buildRequest(false))).data
    notifySuccess(i18n.t(labelKeys.done, { count: result.service_count }))
    emit('update')
  } finally {
    loading.value = false
  }
}

const onBack = () => {
  const index = importSteps.indexOf(step.value)
  if (index === 0) emit('cancel')
  else step.value = importSteps[index - 1]
}

</script>
<template>
  <q-card v-drag-dialog="!isMobile" class="dialog-card-flex">
    <navigate-back
      :text="i18n.t(labelKeys.title)"
      @cancel="emit('cancel')"
    />
    <q-card-section class="scroll col q-pa-none dialog-card-content-scroll">
      <q-card-section class="q-px-sm">
        <q-stepper
          v-model="step"
          class="q-py-none"
          animated
        >
          <q-step
            :name="importStepEnum.FILE"
            :title="i18n.t('common.file')"
            icon="mdi-file-delimited-outline"
            :done="stepDone(importStepEnum.FILE)"
            :header-nav="stepDone(importStepEnum.FILE)"
          >
            <div class="row q-col-gutter-sm">
              <div :class="fieldClass(4)">
                <q-file
                  :model-value="file"
                  accept=".csv,.txt,.xlsx"
                  outlined
                  dense
                  clearable
                  :loading="loading"
                  :label="i18n.t('common.file')"
                  @update:model-value="onFileSelected"
                >
                  <template #prepend>
                    <q-icon name="mdi-paperclip" />
                  </template>
                </q-file>
              </div>
              <div :class="fieldClass(8)" class="column q-gutter-sm">
                <info-box v-if="!analysis" :id="infoBoxIdEnum.SERVICE_IMPORT_FILE_HINT" :message="i18n.t(labelKeys.fileHint)" />
                <info-box
                  v-if="analysis"
                  :tone="isAnalysisMatched ? infoBoxToneEnum.SUCCESS : infoBoxToneEnum.INFO"
                  :message="analysis.is_reelsync
                    ? i18n.t('common.equipmentImport.reelsyncDetected', { rows: analysis.row_count })
                    : i18n.t('common.equipmentImport.columnsRecognized', { rows: analysis.row_count, matched: analysis.matched_count, total: analysis.columns.length })"
                />
                <info-box
                  v-if="analysis?.has_groups"
                  :tone="infoBoxToneEnum.WARNING"
                  :message="i18n.t('common.equipmentImport.groupsDetected', { count: analysis.group_count })"
                />
                <info-box
                  v-if="analysis?.address_merge"
                  :tone="infoBoxToneEnum.WARNING"
                  :message="i18n.t('common.staffImport.addressMerge')"
                />
                <info-box
                  v-if="detectedOtherType"
                  :tone="infoBoxToneEnum.WARNING"
                  :message="i18n.t('common.equipmentImport.typeMismatch', { type: i18n.t(analysis.service_type === serviceTypeEnum.STAFF ? 'common.staff' : 'common.equipment', 1) })"
                />
              </div>
            </div>
            <stepper-navigation
              :continue="() => step = importStepEnum.MAPPING"
              :disable="!analysis"
            />
          </q-step>

          <q-step
            :name="importStepEnum.MAPPING"
            :title="i18n.t('common.equipmentImport.mapping')"
            icon="mdi-table-arrow-right"
            :done="stepDone(importStepEnum.MAPPING)"
            :header-nav="stepDone(importStepEnum.MAPPING)"
          >
            <div v-if="analysis" class="row q-col-gutter-sm">
              <div :class="fieldClass(6)" class="row q-col-gutter-sm items-start content-start">
                <q-select
                  v-model="numberFormat"
                  :options="numberFormatOptions"
                  option-value="id"
                  option-label="name"
                  emit-value
                  map-options
                  outlined
                  dense
                  :label="i18n.t('common.equipmentImport.numberFormat')"
                  :class="fieldClass(6)"
                />
                <q-select
                  v-model="dateFormat"
                  :options="dateFormatOptions"
                  option-value="id"
                  option-label="name"
                  emit-value
                  map-options
                  outlined
                  dense
                  :label="i18n.t('common.equipmentImport.dateFormat')"
                  :class="fieldClass(6)"
                />
              </div>
              <div :class="fieldClass(6)" class="column q-gutter-sm">
                <info-box
                  v-if="!isNameMapped"
                  :tone="infoBoxToneEnum.WARNING"
                  :message="i18n.t('common.equipmentImport.nameRequired')"
                />
                <info-box
                  v-for="column in confirmationColumns"
                  :key="column.index"
                  :tone="infoBoxToneEnum.QUESTION"
                >
                  {{ i18n.t('common.equipmentImport.confirmPurchasePrice', { column: column.name }) }}
                  <template #action>
                    <q-btn
                      flat
                      :color="confirmations[column.index] === false ? 'primary' : undefined"
                      :icon="confirmations[column.index] === false ? 'mdi-check' : undefined"
                      :label="i18n.t('common.no')"
                      @click="onConfirmationAnswered(column, false)"
                    />
                    <q-btn
                      flat
                      :color="confirmations[column.index] === true ? 'primary' : undefined"
                      :icon="confirmations[column.index] === true ? 'mdi-check' : undefined"
                      :label="i18n.t('common.yes')"
                      @click="onConfirmationAnswered(column, true)"
                    />
                  </template>
                </info-box>
              </div>
              <div class="col-12">
                <q-markup-table
                  flat
                  bordered
                  dense
                  separator="horizontal"
                  class="mapping-table"
                >
                  <thead>
                    <tr>
                      <th class="text-left" style="width: 30%;">{{ i18n.t('common.equipmentImport.column') }}</th>
                      <th class="text-left">
                        <div class="row items-center no-wrap">
                          <span>{{ i18n.t('common.equipmentImport.samples') }}</span>
                          <q-btn
                            flat
                            dense
                            round
                            size="sm"
                            icon="mdi-chevron-left"
                            :disable="samplePage === 0"
                            @click="samplePage--"
                          />
                          <span v-if="sampleRowCount" class="text-primary">{{ i18n.t('common.row', 1) }} {{ samplePage + FIRST_DATA_ROW_NUMBER }}</span>
                          <q-btn
                            flat
                            dense
                            round
                            size="sm"
                            icon="mdi-chevron-right"
                            :disable="samplePage + 1 >= sampleRowCount"
                            @click="samplePage++"
                          />
                        </div>
                      </th>
                      <th class="text-left" style="width: 16rem;">{{ i18n.t('common.equipmentImport.target.title') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(column, index) in analysis.columns"
                      :key="index"
                      :class="{ 'text-grey-6': targets[index] === serviceImportTargetEnum.SKIP }"
                    >
                      <td class="text-weight-medium">{{ column.name }}</td>
                      <td class="text-caption" :class="{ 'text-primary': targets[index] !== serviceImportTargetEnum.SKIP }">{{ sampleRows[samplePage]?.[index] }}</td>
                      <td>
                        <q-select
                          v-model="targets[index]"
                          :options="getTargetOptions(index)"
                          option-value="id"
                          option-label="name"
                          emit-value
                          map-options
                          dense
                          options-dense
                          borderless
                        />
                      </td>
                    </tr>
                  </tbody>
                </q-markup-table>
              </div>
            </div>
            <stepper-navigation
              :back="onBack"
              :continue="() => step = importStepEnum.LOCATION"
              :disable="!isMappingValid"
            />
          </q-step>

          <q-step
            :name="importStepEnum.LOCATION"
            :title="i18n.t('common.warehouse')"
            icon="mdi-warehouse"
            :done="stepDone(importStepEnum.LOCATION)"
            :header-nav="stepDone(importStepEnum.LOCATION)"
          >
            <div class="row q-col-gutter-sm">
              <div :class="fieldClass(4)">
                <warehouse-selector
                  v-model="warehouse"
                  :kind="planningStore.warehouseKindEnum.PHYSICAL"
                  :label="i18n.t(labelKeys.defaultLocation)"
                  required
                />
                <q-toggle
                  v-model="collisionMode"
                  :true-value="serviceImportCodeCollisionModeEnum.NEW_CODE"
                  :false-value="serviceImportCodeCollisionModeEnum.SKIP"
                  :label="i18n.t(collisionMode === serviceImportCodeCollisionModeEnum.NEW_CODE ? 'common.equipmentImport.collisionNewCode' : 'common.equipmentImport.collisionSkip')"
                  class="q-mt-md"
                />
              </div>
              <div :class="fieldClass(8)">
                <info-box :id="infoBoxIdEnum.SERVICE_IMPORT_LOCATION_HINT" :message="i18n.t(labelKeys.locationHint)" />
              </div>
            </div>
            <stepper-navigation :back="onBack">
              <q-btn
                color="primary"
                icon-right="mdi-chevron-right"
                :label="i18n.t('common.continue')"
                :disable="!warehouse"
                :loading="loading"
                @click="onLocationContinue"
              />
            </stepper-navigation>
          </q-step>

          <q-step
            :name="importStepEnum.SUMMARY"
            :title="i18n.t('common.preview')"
            icon="mdi-clipboard-check-outline"
          >
            <template v-if="summary">
              <div class="import-stats q-mb-md">
                <div v-for="field in summaryFields" :key="field.label" class="import-stat">
                  <div class="import-stat-value text-h5 text-primary text-weight-medium">{{ field.value }}</div>
                  <div class="text-caption text-grey-7">{{ i18n.t(field.label) }}</div>
                </div>
              </div>
              <q-list v-if="summarySections.length" class="import-sections">
                <q-expansion-item
                  v-for="section in summarySections"
                  :key="section.key"
                  :default-opened="section.attention"
                  expand-separator
                >
                  <template #header>
                    <q-item-section avatar>
                      <q-icon :name="section.icon" :color="section.attention ? 'warning' : 'grey-7'" />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label>{{ i18n.t(section.label) }}</q-item-label>
                    </q-item-section>
                    <q-item-section side>
                      <q-avatar :color="section.attention ? 'warning' : 'grey-6'" text-color="white" size="sm">
                        <span class="text-caption text-weight-bold">{{ section.count }}</span>
                      </q-avatar>
                    </q-item-section>
                  </template>
                  <div class="q-px-md q-pb-md">
                    <div v-if="section.chips" class="row q-gutter-xs">
                      <q-chip
                        v-for="chip in section.chips"
                        :key="chip"
                        dense
                        square
                      >
                        {{ chip }}
                      </q-chip>
                    </div>
                    <q-markup-table
                      v-else
                      flat
                      dense
                      class="import-section-table"
                    >
                      <tbody>
                        <tr v-for="(row, index) in section.rows" :key="index">
                          <td v-for="(cell, cellIndex) in row" :key="cellIndex">{{ cell }}</td>
                        </tr>
                      </tbody>
                    </q-markup-table>
                  </div>
                </q-expansion-item>
              </q-list>
            </template>
            <stepper-navigation
              :back="onBack"
              :finish="onImport"
              :finish-label="i18n.t(labelKeys.confirm, { count: summary?.service_count || 0 })"
              icon="mdi-database-import-outline"
              :icon-right="null"
              :disable="!summary?.service_count"
              :loading="loading"
            />
          </q-step>
        </q-stepper>
      </q-card-section>
    </q-card-section>
  </q-card>
</template>
<style scoped>
.import-stats {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
  gap: .75rem;
}
.import-stat {
  padding: .75rem 1rem;
  border-radius: .5rem;
  border: 1px solid color-mix(in srgb, var(--q-primary) 15%, transparent);
  box-shadow: 0 1px 3px rgba(0, 0, 0, .06);
}
.import-stat-value {
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}
.import-sections {
  border-radius: .5rem;
  border: 1px solid color-mix(in srgb, var(--q-primary) 15%, transparent);
  overflow: hidden;
}
.import-section-table :deep(td) {
  white-space: normal;
}
.mapping-table :deep(table) {
  table-layout: fixed;
  width: 100%;
}
.mapping-table :deep(td) {
  white-space: normal;
  overflow-wrap: anywhere;
}
</style>
